/** @jsxImportSource @emotion/react */
import {
  Badge,
  Box,
  Divider,
  IconButton,
  List,
  ListItem,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Tabs,
  Typography,
  useTheme,
} from '@mui/material'
import { useState } from 'react'
import { useAtomValue } from 'jotai'
import { ManageAccounts } from '@mui/icons-material'

import { Button } from '@app/components/Atoms/Button'
import { Icon } from '@app/components/Atoms/Icon/Icon'
import { Autocomplete } from '@app/components/Atoms/Autocomplete/Autocomplete'
import { ManageUserRolesDrawer } from '../components/Admin.ManageUserRolesDrawer'
import { LoadingSpinner } from '@app/components/ui/LoadingSpinner/LoadingSpinner'

import {
  FEATURE_FLAGS,
  ROLE_COLORS,
  SELECTABLE_ROLES,
  USER_ROLES_LABELS_MAP,
} from '@app/app-constants'
import { LAYER_TYPE_COLORS } from '../utils/Admin.utils'
import { clientConfigAtom } from '@app/state/clientsAtom'
import { LayerPermissionsResponse, UserRole } from '@app/types'
import { useFeatureFlags } from '@app/hooks/useFeatureFlags'
import { useFetchCustomUserRoles } from '../hooks/useCustomUserRoles'
import { useFetchLayerPermissions, useUpdateLayerPermissions } from '../hooks/useLayerPermissions'

import {
  errorLayerPermissions,
  headerButtonsContainerStyle,
  headerSection,
  layerPermissionsDivider,
  layerPermissionsHeader,
  layerPermissionsHeaderInner,
  layerPermissionsTable,
  layerPermissionsTabsWrapper,
  layerPermissionsTypography,
  layerPermissionsTypographyUpper,
  layerPermissionsWrapper,
  roleListItem,
  rolesList,
} from '../styles/Admin.styles'

export const LayerPermissionsPage = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [page, setPage] = useState(0)
  const [pageSize, setPageSize] = useState(25)
  const [layerType, setLayerType] = useState('all')
  const [openManageRolesDrawer, setOpenManageRolesDrawer] = useState(false)
  const [editingLayer, setEditingLayer] = useState<number | null>(null)
  const [rolesForEdit, setRolesForEdit] = useState<UserRole[]>([UserRole.MEMBER, UserRole.VIEWER])
  const clientConfig = useAtomValue(clientConfigAtom)
  const public_access = clientConfig ? clientConfig.public_access : false

  const theme = useTheme()
  const { useFeatureFlag } = useFeatureFlags()
  const updateLayerPermissionsMutation = useUpdateLayerPermissions()

  const {
    data: customUserRoles,
    isLoading: customRolesLoading,
    isError: customRolesError,
  } = useFetchCustomUserRoles()

  const {
    data: layerPermissions,
    isLoading: layerPermissionsLoading,
    isError: layerPermissionsError,
    refetch: refetchLayerPermissions,
  } = useFetchLayerPermissions(page + 1, pageSize, layerType)

  const canManageCustomUserRoles = useFeatureFlag(FEATURE_FLAGS.CUSTOM_USER_ROLES)
  const isLoading = layerPermissionsLoading || customRolesLoading
  const isError = customRolesError || layerPermissionsError

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    setPageSize(parseInt(event.target.value, 10))
    setPage(0)
  }

  const handleChangeRoles = (roles: UserRole[]) => {
    if (!roles.includes(UserRole.ADMIN)) {
      setRolesForEdit([...roles, UserRole.ADMIN])
    } else {
      setRolesForEdit(roles)
    }
  }

  if (isError) {
    return (
      <Box css={errorLayerPermissions}>Something went wrong, please try refreshing the page</Box>
    )
  }

  return (
    <Box css={layerPermissionsWrapper}>
      <Box css={headerSection}>
        <Box css={headerButtonsContainerStyle}>
          {canManageCustomUserRoles ? (
            <Button
              variant="contained"
              size="small"
              onClick={() => setOpenManageRolesDrawer(true)}
              startIcon={<ManageAccounts />}
            >
              Manage Roles
            </Button>
          ) : null}
        </Box>
      </Box>
      <Box css={layerPermissionsHeader}>
        <Box component={Paper} css={layerPermissionsHeaderInner({ theme })}>
          <Typography variant="h5" css={layerPermissionsTypographyUpper({ theme })}>
            Layer Permissions
          </Typography>
          <Typography variant="body2" css={layerPermissionsTypography({ theme })}>
            In this area you can manage layer permissions for individual layers. Attach roles to
            users in the Manage Users tab. Create custom roles by selecting the Manage Roles button
            above.
          </Typography>
        </Box>
        {isLoading ? <LoadingSpinner /> : null}

        <Box css={layerPermissionsTabsWrapper} component={Paper}>
          <Tabs
            value={activeTab}
            onChange={(_: React.SyntheticEvent, newValue: number) => setActiveTab(newValue)}
            textColor="primary"
            indicatorColor="primary"
          >
            <Tab
              label="All Layer Types"
              id="tab-1"
              aria-controls="tabpanel-1"
              onClick={() => {
                setLayerType('all')
                refetchLayerPermissions()
              }}
            />
            <Tab
              label="Elements"
              id="tab-1"
              aria-controls="tabpanel-1"
              onClick={() => {
                setLayerType('asset')
                refetchLayerPermissions()
              }}
            />
            <Tab
              label="Hazards"
              id="tab-1"
              aria-controls="tabpanel-1"
              onClick={() => {
                setLayerType('hazard')
                refetchLayerPermissions()
              }}
            />
            <Tab
              label="Contextual"
              id="tab-1"
              aria-controls="tabpanel-1"
              onClick={() => {
                setLayerType('information')
                refetchLayerPermissions()
              }}
            />
          </Tabs>
          <Divider css={layerPermissionsDivider} />

          <TableContainer component={Paper}>
            <Table size="small" css={layerPermissionsTable}>
              <TableHead>
                <TableRow css={{ height: '48px' }}>
                  <TableCell css={{ width: '5%' }}>ID</TableCell>
                  <TableCell css={{ width: '40%' }}>Title</TableCell>
                  <TableCell align={'center'} css={{ width: '10%' }}>
                    Type
                  </TableCell>
                  <TableCell align={'center'} css={{ width: '10%' }}>
                    User Roles
                  </TableCell>
                  <TableCell align={'center'} css={{ width: '10%' }}>
                    Edit
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {layerPermissions?.map((layer: LayerPermissionsResponse) => {
                  return (
                    <TableRow key={layer.layer_id}>
                      <TableCell>{layer.layer_id}</TableCell>
                      <TableCell>{layer.title}</TableCell>
                      <TableCell>
                        <List css={rolesList}>
                          <ListItem
                            title={layer.layer_type}
                            css={roleListItem}
                            key={`${layer.layer_id}-${layer.layer_type}`}
                          >
                            <Badge
                              color={LAYER_TYPE_COLORS[layer.layer_type] || 'info'}
                              badgeContent={
                                layer.layer_type === 'asset'
                                  ? 'element'
                                  : layer.layer_type === 'information'
                                    ? 'contextual'
                                    : layer.layer_type
                              }
                            />
                          </ListItem>
                        </List>
                      </TableCell>
                      <TableCell align="center" css={{ minWidth: '340px' }}>
                        {editingLayer === layer.layer_id ? (
                          <Autocomplete
                            value={rolesForEdit}
                            options={[
                              ...SELECTABLE_ROLES,
                              ...(customUserRoles
                                ? customUserRoles.map((role) => role.name.toLowerCase() as UserRole)
                                : []),
                              ...(public_access ? ['public'] : []),
                            ]}
                            fixedOptions={['admin']}
                            onChange={(_, value) => handleChangeRoles(value as UserRole[])}
                            labelsMap={{
                              ...USER_ROLES_LABELS_MAP,
                              ...(customUserRoles
                                ? Object.fromEntries(
                                    customUserRoles.map((role) => [
                                      role.name.toLowerCase(),
                                      role.description,
                                    ]),
                                  )
                                : {}),
                              ...(public_access
                                ? { public: 'Makes layer publicly available' }
                                : {}),
                            }}
                            small
                          />
                        ) : (
                          <List css={rolesList}>
                            {layer.user_roles.sort().map((role: string) => {
                              // Filter out non-assignable roles (STAFF)
                              if (role === UserRole.STAFF) return null

                              // Type guard: at this point role can only be VIEWER, MEMBER, or ADMIN
                              const displayableRole = role as
                                | UserRole.VIEWER
                                | UserRole.MEMBER
                                | UserRole.ADMIN

                              return (
                                <ListItem
                                  title={USER_ROLES_LABELS_MAP[displayableRole]}
                                  css={roleListItem}
                                  key={`${layer.layer_id}-${role}`}
                                >
                                  <Badge
                                    color={ROLE_COLORS[displayableRole] || 'info'}
                                    badgeContent={role}
                                  />
                                </ListItem>
                              )
                            })}
                          </List>
                        )}
                      </TableCell>
                      <TableCell align="center" css={{ width: '120px' }}>
                        {editingLayer === layer.layer_id ? (
                          <>
                            <IconButton
                              onClick={() => {
                                updateLayerPermissionsMutation.mutate({
                                  layerId: layer.layer_id,
                                  user_roles: rolesForEdit,
                                })
                                setEditingLayer(null)
                              }}
                              disabled={updateLayerPermissionsMutation.isPending}
                            >
                              <Icon iconName="SaveIcon" />
                            </IconButton>
                            <IconButton
                              onClick={() => {
                                setEditingLayer(null)
                              }}
                            >
                              <Icon iconName="Close" />
                            </IconButton>
                          </>
                        ) : (
                          <IconButton
                            onClick={() => {
                              setEditingLayer(layer.layer_id)
                              setRolesForEdit(layer.user_roles as UserRole[])
                            }}
                            disabled={updateLayerPermissionsMutation.isPending}
                          >
                            <Icon iconName="EditIcon" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  )
                })}
              </TableBody>
            </Table>
            <TablePagination
              component="div"
              count={layerPermissions ? layerPermissions[0]?.total_count || 0 : 0}
              page={page}
              onPageChange={(_, selectedPage) => setPage(selectedPage)}
              rowsPerPage={pageSize}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </TableContainer>
        </Box>

        {layerPermissions?.length === 0 && (
          <Box css={{ textAlign: 'center', padding: '32px' }}>
            <Typography variant="body1" color="text.secondary">
              No layers found. Try adjusting your filter criteria.
            </Typography>
          </Box>
        )}
      </Box>
      <ManageUserRolesDrawer
        opensUserRolesDrawer={openManageRolesDrawer}
        setOpensUserRolesDrawer={setOpenManageRolesDrawer}
        customRoles={customUserRoles}
        customRolesLoading={customRolesLoading}
      />
    </Box>
  )
}
